import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Button } from "@/components/ui/button";
import { TextB, TextItalic, TextUnderline, ListBullets, ListNumbers, TextHOne, TextHTwo, TextHThree } from "@phosphor-icons/react";
import { useEffect } from "react";

export default function RichTextEditor({ value, onChange, testId }) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-invert max-w-none focus:outline-none",
        "data-testid": testId || "rich-text-editor",
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML() && value !== undefined) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const btnBase =
    "h-8 w-8 p-0 flex items-center justify-center rounded-sm border border-transparent";
  const active = (on) => (on ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground");

  return (
    <div className="border border-border rounded-md bg-card">
      <div className="flex flex-wrap items-center gap-1 border-b border-border p-2">
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          className={`${btnBase} ${active(editor.isActive("heading", { level: 1 }))}`} data-testid="rte-h1">
          <TextHOne size={16} weight="bold" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`${btnBase} ${active(editor.isActive("heading", { level: 2 }))}`} data-testid="rte-h2">
          <TextHTwo size={16} weight="bold" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`${btnBase} ${active(editor.isActive("heading", { level: 3 }))}`} data-testid="rte-h3">
          <TextHThree size={16} weight="bold" />
        </button>
        <div className="w-px h-6 bg-border mx-1" />
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()}
          className={`${btnBase} ${active(editor.isActive("bold"))}`} data-testid="rte-bold">
          <TextB size={16} weight="bold" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`${btnBase} ${active(editor.isActive("italic"))}`} data-testid="rte-italic">
          <TextItalic size={16} weight="bold" />
        </button>
        <div className="w-px h-6 bg-border mx-1" />
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`${btnBase} ${active(editor.isActive("bulletList"))}`} data-testid="rte-ul">
          <ListBullets size={16} weight="bold" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`${btnBase} ${active(editor.isActive("orderedList"))}`} data-testid="rte-ol">
          <ListNumbers size={16} weight="bold" />
        </button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
